// @ts-expect-error - virtual module provided by vite-plugin-pwa
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getStaleUpdateNavigationTarget } from '@/lib/staleUpdateNavigation';
import { getDismissalInfo, setDismissal } from '@/lib/updateDismissal';
import { checkServerVersion, forceRefresh, getIsOutdated, onVersionMismatch } from '@/lib/versionManager';

const PERIODIC_CHECK_MS = 4 * 60 * 60 * 1000;
const INITIAL_DELAY_MS = 5 * 60 * 1000;
const VISIBILITY_COOLDOWN_MS = 2 * 60 * 60 * 1000;
const RELOAD_BACKSTOP_MS = 4000;

interface UseAppUpdateReturn {
  showUpdatePrompt: boolean;
  isUpdating: boolean;
  acceptUpdate: () => void;
  dismissUpdate: () => void;
}

/**
 * The one app-update pipeline. Registers the service worker and folds every
 * update signal (version.json polling, tab-visibility checks, service worker
 * needRefresh / controllerchange, chunk-load mismatches) into a single
 * outdated state with a single 24 hour dismissal.
 *
 * It also makes a stale tab self-heal without any prompt interaction: once the
 * build is known to be outdated, internal link clicks become full page
 * navigations, so the browser fetches the fresh deploy instead of dying on a
 * chunk the deploy already removed.
 */
export function useAppUpdate(): UseAppUpdateReturn {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
  } = useRegisterSW({
    onRegisterError(error: Error) {
      console.error('[SW] Registration error:', error);
    },
  });

  const [isOutdated, setIsOutdated] = useState(getIsOutdated());
  const [isDismissed, setIsDismissed] = useState(() => getDismissalInfo() !== null);
  const [isUpdating, setIsUpdating] = useState(false);

  const isCheckingRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const lastVisibilityCheckRef = useRef(0);

  useEffect(() => {
    const unsubscribe = onVersionMismatch(() => setIsOutdated(true));
    if (getIsOutdated()) setIsOutdated(true);
    return unsubscribe;
  }, []);

  const checkForUpdate = useCallback(async (): Promise<boolean> => {
    if (isCheckingRef.current) return false;
    isCheckingRef.current = true;
    try {
      return await checkServerVersion();
    } catch (error) {
      console.error('[Version] Error checking for update:', error);
      return false;
    } finally {
      isCheckingRef.current = false;
    }
  }, []);

  // Service worker signals are deploy hints, then verified against
  // version.json: sw.js can change without the app bundle changing.
  useEffect(() => {
    if (!needRefresh) return;
    void checkForUpdate().then((mismatch) => {
      if (!mismatch) setNeedRefresh(false);
    });
  }, [needRefresh, checkForUpdate, setNeedRefresh]);

  // The generated worker skips waiting, so a deploy takes control mid-session
  // via controllerchange, the fastest available "a new build shipped" signal.
  useEffect(() => {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw?.addEventListener) return;
    const onControllerChange = () => {
      void checkForUpdate();
    };
    sw.addEventListener('controllerchange', onControllerChange);
    return () => sw.removeEventListener('controllerchange', onControllerChange);
  }, [checkForUpdate]);

  useEffect(() => {
    let intervalId: number | undefined;
    const initialTimeout = window.setTimeout(() => {
      if (!getIsOutdated()) void checkForUpdate();
      intervalId = window.setInterval(() => {
        if (!getIsOutdated()) void checkForUpdate();
      }, PERIODIC_CHECK_MS);
    }, INITIAL_DELAY_MS);

    return () => {
      window.clearTimeout(initialTimeout);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [checkForUpdate]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || getIsOutdated()) return;
      const now = Date.now();
      if (now - lastVisibilityCheckRef.current < VISIBILITY_COOLDOWN_MS) return;
      lastVisibilityCheckRef.current = now;
      window.setTimeout(() => {
        void checkForUpdate();
      }, 1000);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkForUpdate]);

  // Runs in the capture phase, so React Router's Link sees defaultPrevented
  // and skips its client side navigation.
  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!getIsOutdated()) return;
      const target = getStaleUpdateNavigationTarget(event);
      if (!target) return;
      event.preventDefault();
      window.location.assign(target);
    };

    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, []);

  useEffect(() => {
    if (!isDismissed) return;
    const interval = window.setInterval(() => {
      if (getDismissalInfo() === null) setIsDismissed(false);
    }, 60 * 1000);
    return () => window.clearInterval(interval);
  }, [isDismissed]);

  const acceptUpdate = useCallback(() => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    setIsUpdating(true);

    window.setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        // Nothing left to try.
      }
    }, RELOAD_BACKSTOP_MS);

    void (async () => {
      try {
        const registration = await navigator.serviceWorker?.getRegistration?.();
        registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
      } catch {
        // No service worker: the refresh below still gets the new build.
      }
      forceRefresh();
    })();
  }, []);

  const dismissUpdate = useCallback(() => {
    setDismissal();
    setIsDismissed(true);
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  const previewRequested =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('previewAppUpdate');

  return {
    showUpdatePrompt: (isOutdated || previewRequested) && !isDismissed,
    isUpdating,
    acceptUpdate,
    dismissUpdate,
  };
}
